'use client'

import { useEffect, useRef } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import Script from 'next/script'

/**
 * Trust / social-proof strip directly beneath the search widget: a headline
 * trust line plus the live Trustpilot "Starter" TrustBox (the same template the
 * C2C reviews section uses). White band that bridges the hero into the content
 * region. Client island (the TrustBox loads via Trustpilot's bootstrap script);
 * the `<a>` inside the widget is the server-rendered / no-JS fallback. Summary
 * copy from `homepage.trust` i18n.
 */
const TRUSTPILOT_SCRIPT_URL =
    'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
const TRUSTPILOT_PROFILE_URL = 'https://www.trustpilot.com/review/www.wanderu.com'
const TRUSTPILOT_BUSINESS_UNIT_ID = '54a40c7700006400057c6e53'
const TRUSTPILOT_STARTER_TEMPLATE_ID = '5613c9cde69ddc09340c6beb'
const TRUSTPILOT_STARTER_TOKEN = '2fa440ee-e1d5-4758-a6bc-ea6d5b6e7619'

/** Format a nexus locale (e.g. "en-us") to Trustpilot's form (e.g. "en-US"). */
function formatTrustpilotLocale(locale: string): string {
    const [lang, region] = locale.split('-')
    return region ? `${lang}-${region.toUpperCase()}` : locale
}

export const HomeTrustStrip = () => {
    const t = useTranslations('homepage.trust')
    const locale = useLocale()
    const widgetRef = useRef<HTMLDivElement>(null)

    const loadWidget = () => {
        const tp = (window as unknown as Record<string, unknown>).Trustpilot as
            | { loadFromElement: (el: HTMLElement, useNewApi: boolean) => void }
            | undefined
        if (tp?.loadFromElement && widgetRef.current) {
            tp.loadFromElement(widgetRef.current, true)
        }
    }

    useEffect(() => {
        loadWidget()
    }, [])

    return (
        <section className="border-b border-[#e5e8ed] bg-white px-4 py-4 md:px-10">
            <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-2 text-center">
                <span className="text-sm font-semibold text-[#5f6368]">{t('summary')}</span>
                {/* Trustpilot Starter TrustBox */}
                <div
                    ref={widgetRef}
                    className="trustpilot-widget w-full"
                    data-locale={formatTrustpilotLocale(locale)}
                    data-template-id={TRUSTPILOT_STARTER_TEMPLATE_ID}
                    data-businessunit-id={TRUSTPILOT_BUSINESS_UNIT_ID}
                    data-theme="light"
                    data-style-height="100%"
                    data-style-width="100%"
                    data-token={TRUSTPILOT_STARTER_TOKEN}
                >
                    <a href={TRUSTPILOT_PROFILE_URL} target="_blank" rel="noopener noreferrer">
                        Trustpilot
                    </a>
                </div>
            </div>
            <Script src={TRUSTPILOT_SCRIPT_URL} strategy="afterInteractive" onLoad={loadWidget} />
        </section>
    )
}
